import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { API_BASE } from '../config';
import { PanelContainer, Title, Subtitle, PrimaryButton, SecondaryButton } from '../ui/common';
import { Banner } from '../components/ui/Banner';
import { useConfirm } from '../components/ui/Modal';
import { useTranslation } from '../translations';
import { maPremium } from '../utils/planFirmy';
import PremiumNotice from '../components/premium/PremiumNotice';
import PremiumBadge from '../components/premium/PremiumBadge';
import { TERMIN_DOMYSLNY_GODZIN, MIN_GODZIN_DOMYSLNYCH, MAX_GODZIN_DOMYSLNYCH } from '../utils/terminWplaty';
import { usePremiumBlad } from '../components/premium/PremiumUpgrade';
import { TylkoOdczytWPrzerwie } from '../components/przerwa/KontoWPrzerwie';

// P3.2 (Stripe Connect, ADR-0001): wypożyczalnia podpina konto Stripe JEDNYM przyciskiem.
// Onboarding (dane firmy, konto bankowe) odbywa się na hostowanej stronie Stripe (Account Link);
// my trzymamy tylko stripe_account_id. Klucczy nie wpisuje się ręcznie (sekrety platformy w env).

const HeaderBlock = styled.div`margin-bottom: var(--sp-6);`;
const Card = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-md);
  padding: var(--sp-5) var(--sp-6);
  margin-bottom: var(--sp-5);
`;
const CardTitle = styled.h3`font-size: var(--fs-md); margin: 0 0 var(--sp-3); color: var(--text);`;
const P = styled.p`margin: 0 0 var(--sp-3); color: var(--text); font-size: var(--fs-base); line-height: 1.55;`;
const Hint = styled.p`margin: var(--sp-2) 0 0; font-size: var(--fs-sm); color: var(--text-muted);`;
const Row = styled.div`display: flex; align-items: center; gap: var(--sp-3); flex-wrap: wrap; margin-top: var(--sp-4);`;
const Badge = styled.span`
  display: inline-flex; align-items: center; gap: 7px;
  padding: 5px var(--sp-3); border-radius: var(--r-pill); font-size: var(--fs-sm); font-weight: 600;
  background: ${p => (p.$ok ? 'var(--success-bg)' : 'var(--warning-bg)')};
  color: ${p => (p.$ok ? 'var(--success)' : 'var(--warning)')};
  border: 1px solid ${p => (p.$ok ? 'var(--success-border)' : 'var(--warning-border)')};
`;
const Dot = styled.span`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${p => (p.$ok ? 'var(--success)' : 'var(--warning)')};
`;
const Mono = styled.code`
  font-size: var(--fs-sm); color: var(--text-muted);
  background: var(--input-bg); border: 1px solid var(--input-border);
  border-radius: var(--r-sm); padding: 2px 7px;
`;
const Divider = styled.div`margin-top: var(--sp-5); padding-top: var(--sp-4); border-top: 1px solid var(--input-border);`;
const SectionTitle = styled(P)`margin-bottom: var(--sp-2); font-weight: 600;`;
const NumberInput = styled.input`
  width: 90px; padding: 9px var(--sp-3); border: 1px solid var(--input-border);
  border-radius: var(--r-sm); background: var(--input-bg); color: var(--text); font-size: var(--fs-base);
  &[aria-invalid='true'] { border-color: var(--danger); }
`;
const FieldError = styled.p`margin: var(--sp-2) 0 0; font-size: var(--fs-sm); color: var(--danger); font-weight: 500;`;
const Muted = styled.span`color: var(--text-muted); font-size: var(--fs-base);`;

// Zaliczka w widgecie (12.10.2026, test odbiorczy płatności): pole trzyma SUROWY TEKST, jak pola kwot
// (utils/amountField.js). Dawniej każdy klawisz przechodził przez Math.max(1, Math.min(100, …)):
// pustego pola nie dało się zostawić (Number('') || 1 = 1), więc Backspace na „30” dawał 3 → 1 → 1,
// a dopisane „50” robiło z tego 150 → 100. Wartość sprawdzamy po opuszczeniu pola i przy zapisie.
// Zwraca liczbę całkowitą od 1 do 100 albo null.
export function parseAdvancePercent(raw) {
  const txt = String(raw ?? '').trim();
  if (!/^\d{1,3}$/.test(txt)) return null;
  const n = Number(txt);
  return n >= 1 && n <= 100 ? n : null;
}

// Domyślny czas na wpłatę zaliczki z panelu (F.6): liczba całkowita godzin 1-720 albo null.
// Surowy tekst w polu, sprawdzany po opuszczeniu pola i przy zapisie, jak zaliczka wyżej.
export function parseDeadlineHours(raw) {
  const txt = String(raw ?? '').trim();
  if (!/^\d{1,3}$/.test(txt)) return null;
  const n = Number(txt);
  return n >= MIN_GODZIN_DOMYSLNYCH && n <= MAX_GODZIN_DOMYSLNYCH ? n : null;
}

function PaymentSettings() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const pokazBladPremium = usePremiumBlad();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [banner, setBanner] = useState(null);
  // U.12: czy serwer platformy ma skonfigurowany klucz Stripe (STRIPE_SECRET_KEY).
  // Bez niego onboarding i płatności zwracają błąd — pokazujemy to z góry zamiast po kliknięciu.
  const [serverStripeReady, setServerStripeReady] = useState(true);
  const [advancePct, setAdvancePct] = useState('100');
  // Komunikat zakresu pokazujemy po opuszczeniu pola albo po próbie zapisu, nie w trakcie pisania.
  const [advanceChecked, setAdvanceChecked] = useState(false);
  const [savingPct, setSavingPct] = useState(false);
  // Ustawień nie udało się wczytać (403 dla pracownika, błąd serwera). Wtedy NIE rysujemy ekranu
  // „Połącz ze Stripe”: brak `settings` znaczyłby „firma nie ma konta”, a to nieprawda (12.10.2026).
  const [loadFailed, setLoadFailed] = useState(null); // null | 'forbidden' | 'error'
  const [cancelHours, setCancelHours] = useState('');
  const [savingHours, setSavingHours] = useState(false);
  // F.6: domyślny czas na wpłatę zaliczki z panelu (termin przy linku do zaliczki).
  const [deadlineHours, setDeadlineHours] = useState(String(TERMIN_DOMYSLNY_GODZIN));
  const [deadlineChecked, setDeadlineChecked] = useState(false);
  const [savingDeadline, setSavingDeadline] = useState(false);
  // Plan firmy z odpowiedzi GET (F.5), ważniejszy niż zapis z logowania w localStorage.
  const [planFirmy, setPlanFirmy] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/payment-settings`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setLoadFailed(res.status === 403 ? 'forbidden' : 'error');
        return;
      }
      setLoadFailed(null);
      setSettings(data.settings || null);
      setPlanFirmy(typeof data.organizationPlan === 'string' ? data.organizationPlan : null);
      // Domyślnie true, żeby starszy backend (bez pola) nie pokazywał fałszywego alarmu.
      setServerStripeReady(data.serverStripeConfigured !== false);
      const pct = Number(data.settings && data.settings.widget_advance_percent);
      if (Number.isInteger(pct) && pct >= 1 && pct <= 100) setAdvancePct(String(pct));
      const hrs = data.settings && data.settings.unpaid_reservation_cancel_hours;
      setCancelHours(hrs == null ? '' : String(hrs));
      const termin = parseDeadlineHours(data.settings && data.settings.payment_deadline_default_hours);
      setDeadlineHours(String(termin || TERMIN_DOMYSLNY_GODZIN));
    } catch (e) {
      setLoadFailed('error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Powrót ze Stripe. `connected` to zwykły powrót (także po porzuceniu konfiguracji), `refresh`
    // przychodzi, gdy link do konfiguracji wygasł albo był już użyty: wtedy mówimy, co zrobić.
    const params = new URLSearchParams(window.location.search);
    if (params.get('refresh')) {
      setBanner({ ok: false, tone: 'warning', msg: t('paymentSettings.stripeLinkExpired') });
    } else if (params.get('connected')) {
      setBanner({ ok: true, msg: t('paymentSettings.returnedFromStripe') });
    }
  }, [load, t]);

  const onboard = async () => {
    setWorking(true);
    setBanner(null);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/payments/stripe/onboard`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok && pokazBladPremium(data)) { setWorking(false); return; }
      if (!res.ok || !data.url) throw new Error(data.error || t('paymentSettings.onboardFailed'));
      window.location.href = data.url; // hostowany onboarding Stripe
    } catch (e) {
      setWorking(false);
      setBanner({ ok: false, msg: e.message || t('common.error') });
    }
  };

  const saveCancelHours = async () => {
    setSavingHours(true);
    setBanner(null);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/payment-settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify({ unpaid_reservation_cancel_hours: cancelHours === '' ? null : Number(cancelHours) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('paymentSettings.saveError'));
      setBanner({ ok: true, msg: cancelHours === '' ? t('paymentSettings.autoCancelDisabled') : t('paymentSettings.autoCancelSaved', { hours: cancelHours }) });
    } catch (e) {
      setBanner({ ok: false, msg: e.message || t('paymentSettings.saveError') });
    } finally {
      setSavingHours(false);
    }
  };

  const disconnect = async () => {
    const sure = await confirm({
      title: t('paymentSettings.disconnectConfirmTitle'),
      message: t('paymentSettings.disconnectConfirmMessage'),
      tone: 'danger',
      confirmLabel: t('paymentSettings.disconnectButton'),
    });
    if (!sure) return;
    setWorking(true);
    setBanner(null);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/payments/stripe/disconnect`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('paymentSettings.disconnectFailed'));
      setBanner({ ok: true, msg: t('paymentSettings.disconnectedSuccess') });
      setLoading(true);
      await load();
    } catch (e) {
      setBanner({ ok: false, msg: e.message || t('common.error') });
    } finally {
      setWorking(false);
    }
  };

  // P3.18: miękka pauza (zachowuje konto Stripe, w odróżnieniu od disconnect).
  const pauseToggle = async () => {
    const nextPaused = !(settings && settings.online_paused);
    if (nextPaused) {
      const sure = await confirm({
        title: t('paymentSettings.pauseConfirmTitle'),
        message: t('paymentSettings.pauseConfirmMessage'),
        tone: 'primary',
        confirmLabel: t('paymentSettings.pauseOnline'),
      });
      if (!sure) return;
    }
    setWorking(true);
    setBanner(null);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/payments/stripe/pause`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify({ paused: nextPaused }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('paymentSettings.pauseFailed'));
      setBanner({ ok: true, msg: nextPaused ? t('paymentSettings.pausedSuccess') : t('paymentSettings.resumedSuccess') });
      setLoading(true);
      await load();
    } catch (e) {
      setBanner({ ok: false, msg: e.message || t('common.error') });
    } finally {
      setWorking(false);
    }
  };

  const saveDeadlineHours = async () => {
    setDeadlineChecked(true);
    const godziny = parseDeadlineHours(deadlineHours);
    if (godziny === null) return; // komunikat stoi pod polem
    setSavingDeadline(true);
    setBanner(null);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/payment-settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify({ payment_deadline_default_hours: godziny }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('paymentSettings.saveError'));
      setDeadlineHours(String(godziny));
      setBanner({ ok: true, msg: t('paymentSettings.deadlineSaved', { hours: godziny }) });
    } catch (e) {
      setBanner({ ok: false, msg: e.message || t('paymentSettings.saveError') });
    } finally {
      setSavingDeadline(false);
    }
  };

  const savePct = async () => {
    setAdvanceChecked(true);
    const pct = parseAdvancePercent(advancePct);
    if (pct === null) return; // komunikat stoi pod polem
    setSavingPct(true);
    setBanner(null);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/payment-settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify({ widget_advance_percent: pct }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('paymentSettings.saveError'));
      setAdvancePct(String(pct));
      // Komunikat podaje zapisaną wartość: „Zapisano” bez liczby nie pozwalał zauważyć pomyłki.
      setBanner({ ok: true, msg: t('paymentSettings.advanceSaved', { percent: pct }) });
    } catch (e) {
      setBanner({ ok: false, msg: e.message || t('paymentSettings.saveError') });
    } finally {
      setSavingPct(false);
    }
  };

  if (loading) return <PanelContainer><Subtitle>{t('paymentSettings.loading')}</Subtitle></PanelContainer>;

  if (loadFailed) {
    return (
      <PanelContainer>
        <HeaderBlock>
          <Title>{t('paymentSettings.title')}</Title>
          <Subtitle>{t('paymentSettings.subtitle')}</Subtitle>
        </HeaderBlock>
        <Card data-cy="payment-settings-load-failed">
          {loadFailed === 'forbidden' ? (
            <Banner tone="info">{t('paymentSettings.adminOnly')}</Banner>
          ) : (
            <Banner tone="danger" action={{ label: t('common.retry'), onClick: () => { setLoading(true); load(); } }}>
              {t('paymentSettings.loadError')}
            </Banner>
          )}
        </Card>
      </PanelContainer>
    );
  }

  const acc = settings && settings.stripe;
  const accountId = (acc && acc.account_id) || (settings && settings.stripe_account_id) || null;
  const active = !!(acc && acc.charges_enabled); // konto Stripe zdolne przyjmować płatności
  const paused = !!(settings && settings.online_paused); // P3.18: miękka pauza (konto zostaje)
  const acceptingOnline = active && !paused; // realnie przyjmujemy płatności online
  // Stanu konta nie znamy (12.10.2026, test odbiorczy płatności): API Stripe nie odpowiedziało i nie
  // ma ostatniego znanego stanu (`status_unknown`), albo serwer nie ma klucza Stripe. Wtedy NIE
  // piszemy „Konfiguracja niedokończona”, bo aktywnej firmie mówiło to, że Stripe czeka na jej dane.
  // `stale` = pokazujemy ostatni znany stan, bo świeżego odczytu nie było.
  const statusUnknown = !!accountId && (!serverStripeReady || !acc || !!acc.status_unknown);
  const statusStale = !statusUnknown && !!(acc && acc.stale);
  // F.5: nowe linki do zapłaty i podłączenie konta Stripe są w Premium (serwer odpowiada 403).
  // Firma, która zeszła z Premium z podłączonym kontem, widzi jego stan, może dokończyć jego
  // konfigurację, wstrzymać je albo odłączyć; wysłane linki, zwroty i kaucje działają dalej.
  const premium = maPremium(planFirmy);
  // Zaliczka i auto-anulowanie dotyczą WYŁĄCZNIE rezerwacji z widgetu, a widget jest w planie premium
  // (super-admin widzi wszystko, decyzja z 26.09.2026). Na planie basic te sekcje nic nie zmieniały.
  const widgetAvailable = premium;
  const advanceInvalid = advanceChecked && parseAdvancePercent(advancePct) === null;
  const deadlineInvalid = deadlineChecked && parseDeadlineHours(deadlineHours) === null;
  const bannerEl = banner && (
    <div style={{ marginBottom: 'var(--sp-4)' }} data-cy="payment-settings-banner">
      <Banner tone={banner.tone || (banner.ok ? 'success' : 'danger')} compact>{banner.msg}</Banner>
    </div>
  );

  return (
    <PanelContainer>
      <HeaderBlock data-tour="payment-settings-intro">
        <Title>{t('paymentSettings.title')}</Title>
        <Subtitle>{t('paymentSettings.subtitle')}</Subtitle>
      </HeaderBlock>
      <TylkoOdczytWPrzerwie>

      <Card data-tour="payment-settings-account">
        <CardTitle>{t('paymentSettings.cardTitle')}</CardTitle>

        {bannerEl}

        {!serverStripeReady && (
          <div style={{ marginBottom: 'var(--sp-4)' }}>
            <Banner tone="warning" title={t('paymentSettings.serverMissingTitle')} data-cy="stripe-server-missing">
              {t('paymentSettings.serverMissingBody', { envVar: 'STRIPE_SECRET_KEY', testKey: 'sk_test_…' })}
            </Banner>
          </div>
        )}

        {!accountId && !premium && (
          <PremiumNotice feature="payment_links" title={t('paymentSettings.premiumTitle')} data-cy="payment-settings-premium">
            {t('paymentSettings.premiumBody')}
          </PremiumNotice>
        )}

        {!accountId && premium && (
          <>
            <P>{t('paymentSettings.onboardIntro')}</P>
            <ol style={{ margin: '0 0 4px', paddingLeft: 20, color: 'var(--text)', fontSize: 'var(--fs-md)', lineHeight: 1.7 }}>
              <li>{t('paymentSettings.step1')}</li>
              <li>{t('paymentSettings.step2')}</li>
              <li>{t('paymentSettings.step3')}</li>
            </ol>
            <Row>
              <PrimaryButton onClick={onboard} disabled={working || !serverStripeReady}>
                {working ? t('paymentSettings.redirecting') : t('paymentSettings.connectButton')}
              </PrimaryButton>
            </Row>
          </>
        )}

        {accountId && (
          <>
            <Row style={{ marginTop: 0 }}>
              <Badge $ok={acceptingOnline}>
                <Dot $ok={acceptingOnline} />
                {statusUnknown ? t('paymentSettings.statusUnknown') : !active ? t('paymentSettings.statusIncomplete') : paused ? t('paymentSettings.statusPaused') : t('paymentSettings.statusActive')}
              </Badge>
              <Mono>{accountId}</Mono>
            </Row>
            {statusUnknown && (
              <>
                <Hint data-cy="payment-settings-status-unknown">{t('paymentSettings.statusUnknownHint')}</Hint>
                {serverStripeReady && (
                  <Row>
                    <SecondaryButton onClick={() => { setLoading(true); load(); }} disabled={working}>{t('paymentSettings.refreshStatus')}</SecondaryButton>
                  </Row>
                )}
              </>
            )}
            {statusStale && <Hint data-cy="payment-settings-status-stale">{t('paymentSettings.statusStaleHint')}</Hint>}
            {!active && !statusUnknown && (
              <>
                <Hint>{t('paymentSettings.needsMoreDataHint')}</Hint>
                <Row>
                  {/* Także na Basic: konfiguracja konta już podłączonego nie wymaga Premium (serwer
                      bramkuje tylko nowe podłączenie), a bez niej wysłane linki nie wróciłyby do działania. */}
                  <PrimaryButton onClick={onboard} disabled={working || !serverStripeReady}>
                    {working ? t('paymentSettings.redirecting') : t('paymentSettings.finishSetup')}
                  </PrimaryButton>
                  <SecondaryButton onClick={() => { setLoading(true); load(); }} disabled={working}>{t('paymentSettings.refreshStatus')}</SecondaryButton>
                </Row>
              </>
            )}
            {!premium && (
              <div style={{ marginTop: 'var(--sp-4)' }}>
                <PremiumNotice feature="payment_links" title={t('paymentSettings.premiumTitle')} data-cy="payment-settings-premium">
                  {t('paymentSettings.premiumConnectedBody')}
                </PremiumNotice>
              </div>
            )}
            {active && !paused && <Hint>{t('paymentSettings.activeHint')}</Hint>}
            {active && paused && <Hint>{t('paymentSettings.pausedHint')}</Hint>}

            {widgetAvailable ? (
              <>
                <Divider>
                  <SectionTitle as="label" htmlFor="widget-advance-percent">{t('paymentSettings.advanceSectionTitle')}</SectionTitle>
                  <Hint style={{ margin: '0 0 10px' }}>{t('paymentSettings.advanceSectionHint')}</Hint>
                  <Row style={{ marginTop: 0 }}>
                    <NumberInput
                      id="widget-advance-percent"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={advancePct}
                      onChange={(e) => { setAdvancePct(e.target.value); setAdvanceChecked(false); }}
                      onBlur={() => setAdvanceChecked(true)}
                      aria-invalid={advanceInvalid || undefined}
                      aria-describedby={advanceInvalid ? 'widget-advance-error' : undefined}
                      data-cy="widget-advance-percent"
                    />
                    <Muted>%</Muted>
                    <SecondaryButton onClick={savePct} disabled={savingPct || working} data-cy="widget-advance-save">{savingPct ? t('paymentSettings.saving') : t('paymentSettings.save')}</SecondaryButton>
                  </Row>
                  {advanceInvalid && (
                    <FieldError id="widget-advance-error" role="alert" data-cy="widget-advance-error">{t('paymentSettings.advanceRange')}</FieldError>
                  )}
                </Divider>

                <Divider>
                  <SectionTitle>{t('paymentSettings.autoCancelSectionTitle')}</SectionTitle>
                  <Hint style={{ margin: '0 0 10px' }}>{t('paymentSettings.autoCancelHint')}</Hint>
                  <Row style={{ marginTop: 0 }}>
                    {/* Bez podpowiedzi „24” w pustym polu: na telefonie szary placeholder wyglądał jak
                        ustawiona wartość, choć funkcja była wyłączona. Stan pisze się obok słowami. */}
                    <NumberInput
                      type="number"
                      min={1}
                      max={168}
                      value={cancelHours}
                      aria-label={t('paymentSettings.autoCancelSectionTitle')}
                      data-cy="auto-cancel-hours"
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') return setCancelHours('');
                        setCancelHours(String(Math.max(1, Math.min(168, Math.round(Number(v) || 1)))));
                      }}
                    />
                    <Muted>{t('paymentSettings.hoursUnit')}</Muted>
                    <SecondaryButton onClick={saveCancelHours} disabled={savingHours || working}>{savingHours ? t('paymentSettings.saving') : t('paymentSettings.save')}</SecondaryButton>
                    {cancelHours === '' && <Muted data-cy="auto-cancel-off">{t('paymentSettings.autoCancelOffNow')}</Muted>}
                  </Row>
                </Divider>
              </>
            ) : (
              <Divider data-cy="payment-settings-widget-premium">
                <PremiumBadge />
                <Hint style={{ margin: 'var(--sp-2) 0 0' }}>{t('paymentSettings.widgetSettingsPremium')}</Hint>
              </Divider>
            )}

            {/* F.6: domyślny czas na wpłatę zaliczki z panelu. Termin z automatycznym anulowaniem
                jest w Premium (serwer odmawia przy linku), na Basic sekcja z plakietką. */}
            <Divider data-cy="payment-deadline-settings">
              <SectionTitle as="label" htmlFor="payment-deadline-hours">
                {t('paymentSettings.deadlineSectionTitle')} {!premium && <PremiumBadge />}
              </SectionTitle>
              <Hint style={{ margin: '0 0 10px' }}>{premium ? t('paymentSettings.deadlineSectionHint') : t('paymentSettings.deadlinePremium')}</Hint>
              {premium && (
                <>
                  <Row style={{ marginTop: 0 }}>
                    <NumberInput
                      id="payment-deadline-hours"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={deadlineHours}
                      onChange={(e) => { setDeadlineHours(e.target.value); setDeadlineChecked(false); }}
                      onBlur={() => setDeadlineChecked(true)}
                      aria-invalid={deadlineInvalid || undefined}
                      aria-describedby={deadlineInvalid ? 'payment-deadline-hours-error' : undefined}
                      data-cy="payment-deadline-hours"
                    />
                    <Muted>{t('paymentSettings.hoursUnit')}</Muted>
                    <SecondaryButton onClick={saveDeadlineHours} disabled={savingDeadline || working} data-cy="payment-deadline-hours-save">
                      {savingDeadline ? t('paymentSettings.saving') : t('paymentSettings.save')}
                    </SecondaryButton>
                  </Row>
                  {deadlineInvalid && (
                    <FieldError id="payment-deadline-hours-error" role="alert" data-cy="payment-deadline-hours-error">
                      {t('paymentSettings.deadlineRange', { min: MIN_GODZIN_DOMYSLNYCH, max: MAX_GODZIN_DOMYSLNYCH })}
                    </FieldError>
                  )}
                </>
              )}
            </Divider>

            {/* Pauza przy KAŻDYM podłączonym koncie (12.10.2026): także przy niedokończonej
                konfiguracji albo nieznanym stanie konta firma może świadomie wstrzymać płatności. */}
            <Divider>
              <SectionTitle>{t('paymentSettings.pauseSectionTitle')}</SectionTitle>
              <Hint style={{ margin: '0 0 10px' }}>
                {paused ? t('paymentSettings.pausedNowHint') : t('paymentSettings.pauseAvailableHint')}
              </Hint>
              <SecondaryButton onClick={pauseToggle} disabled={working || savingPct}>
                {working ? t('paymentSettings.processing') : (paused ? t('paymentSettings.resumeOnline') : t('paymentSettings.pauseOnline'))}
              </SecondaryButton>
            </Divider>

            <Divider>
              <SectionTitle>{t('paymentSettings.disconnectSectionTitle')}</SectionTitle>
              <Hint style={{ margin: '0 0 10px' }}>{t('paymentSettings.disconnectHint')}</Hint>
              <SecondaryButton
                onClick={disconnect}
                disabled={working || savingPct}
                style={{ borderColor: 'var(--danger-border)', color: 'var(--danger)' }}
              >
                {working ? t('paymentSettings.processing') : t('paymentSettings.disconnectButton')}
              </SecondaryButton>
            </Divider>
          </>
        )}

      </Card>
      </TylkoOdczytWPrzerwie>
    </PanelContainer>
  );
}

export default PaymentSettings;
